import * as React from 'react'

import type { Map as MapboxMap } from 'mapbox-gl'
import type { MapRef } from 'react-map-gl/mapbox'

import { useLabelFontsReady } from '../../MapBoxViewer/labelTextBox'
import {
  redrawStackedMarkerImage,
  renderMissingMarkerImage,
} from './GLMarkerImageGenerator'
import { stackedMarkerImageId } from './GLStackChipImage'

// Images already redrawn in Inter, per map: a changed stack set redraws only what is new.
const redrawnInInter = new WeakMap<MapboxMap, Set<string>>()

// A stacked circle wears its count, which is canvas text: drawn up front, and again once Inter
// has loaded, since styleimagemissing would have baked the fallback face in for the session.
export function useStackChipImages(
  map: MapRef | undefined,
  data: GeoJSON.FeatureCollection<GeoJSON.Point>,
  density: number,
): void {
  const stackedImageIds = React.useMemo(
    () =>
      [
        ...new Set(
          data.features.flatMap(({ properties }) =>
            properties?.stackCount && properties.iconImage
              ? [properties.iconImage, properties.iconImageHighlighted].map(
                  id => stackedMarkerImageId(id, properties.stackCount),
                )
              : [],
          ),
        ),
      ]
        .sort()
        .join(' '),
    [data],
  )
  const fontsReady = useLabelFontsReady()
  React.useEffect(() => {
    const gl = map?.getMap()
    if (!gl || !stackedImageIds) return
    const redrawn = redrawnInInter.get(gl) ?? new Set<string>()
    redrawnInInter.set(gl, redrawn)
    stackedImageIds.split(' ').forEach(id => {
      if (!fontsReady) {
        renderMissingMarkerImage(gl, id, density)
      } else if (!redrawn.has(id) || !gl.hasImage(id)) {
        redrawn.add(id)
        redrawStackedMarkerImage(gl, id, density)
      }
    })
  }, [map, stackedImageIds, fontsReady, density])
}
